import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { setAlert } from 'routes/AppContainer/actions';
import { OpDivider } from 'new-components/DLS/OpDivider/OpDivider';
import { FormOnSubmitType, OpForm } from 'new-components/DLS/OpForm/OpForm';
import { useDetermineLoginCandidateNamespaces } from 'utils/customHooks/api/useDetermineLoginCandidateNamespaces';
import { useGetSsoAuthUrl } from 'utils/customHooks/api/useGetSsoAuthUrl';
import { sortOptionsAlphabetically } from 'utils/sortOptionsAlphabetically';
import { getWindowLocation } from 'utils/window';

import { UseAltaIdButton } from '../UseAltaIdButton/UseAltaIdButton';

import './LoginSSOEmailForm.scss';

interface LoginSSOEmailFormProps {
  loginRoute: '/login' | '/signin';
  redirectUrl?: string;
}

export const LoginSSOEmailForm = ({
  loginRoute,
  redirectUrl,
}: LoginSSOEmailFormProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [namespaceNicknameOptions, setNamespaceNicknameOptions] = useState<
    { label: string; value: string }[] | null
  >(null);

  const determineLoginCandidateNamespacesMutation =
    useDetermineLoginCandidateNamespaces();

  const getSsoAuthUrlMutation = useGetSsoAuthUrl();

  const onSubmit: FormOnSubmitType = ({
    values: { email, namespaceNickname },
  }) => {
    getSsoAuthUrlMutation.mutate(
      {
        payload: {
          email,
          ...(redirectUrl && { redirectUrl }),
          ...(namespaceNickname && { namespaceNickname }),
        },
      },
      {
        onSuccess: (data) => {
          getWindowLocation().href = data!.authUrl!;
        },
        onError: (error) => {
          if (
            error.statusCode === 401 &&
            error.message === 'Namespace is required.'
          ) {
            dispatch(setAlert('info', error.message, null, { timeout: 3000 }));

            determineLoginCandidateNamespacesMutation.mutate(
              {
                payload: {
                  email,
                },
              },
              {
                onSuccess: (data) => {
                  setNamespaceNicknameOptions(
                    data!.map(({ nickname }) => ({
                      label: nickname!,
                      value: nickname!,
                    })),
                  );
                },
              },
            );
          }
        },
      },
    );
  };

  return (
    <OpForm
      className="login-sso-email-form"
      initialValues={{ email: '', namespaceNickname: '' }}
      onSubmit={onSubmit}
      defaultButtons={false}
      shouldWarnChangesBeforeUnload={false}
      hasError={false}
    >
      <OpForm.Input
        autoFocus
        name="email"
        label={t('Email')}
        rules={[
          {
            type: 'email',
            message: t('Not a valid email') as string,
          },
          {
            required: true,
            message: t('Email is required') as string,
          },
        ]}
      />

      {namespaceNicknameOptions?.length && (
        <OpForm.Select
          name="namespaceNickname"
          label={t('Namespace')}
          options={namespaceNicknameOptions}
          testId="namespace"
          filterSort={sortOptionsAlphabetically}
        />
      )}

      <OpForm.Button
        block
        className="login-sso-email-form__continue-button"
        htmlType="submit"
        type="primary"
        loading={getSsoAuthUrlMutation.isPending}
      >
        {t('Continue')}
      </OpForm.Button>

      <UseAltaIdButton />

      <OpDivider plain>or</OpDivider>

      <OpForm.Button
        block
        onClick={() => {
          // Using navigate here as we don't have access to
          // changeRoute yet since it is in Auth container
          navigate(loginRoute);
        }}
      >
        {t('Sign in without SSO')}
      </OpForm.Button>
    </OpForm>
  );
};
